import { useCallback, useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import { getCurrentUser } from '@/selectors/user.selectors'
import { postWaitlistUser } from './waitlistActions'
import { useSignupPageUpdate } from '@/services/onboardingService'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'

import { SIGNUP_PATHS } from '../helpers'
import { makeNumberSchema, makeReqEmailSchema, getFieldNames } from '@/base'

import { Button, Dialog, Form, IllustrationBlock, Image, Text } from '@/ui'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '@/features/user/signup/SignupFooter'
import { KeepInTouchCard } from '@/features/user/signup/KeepInTouchCard'
import { updateUser } from '@/actions/userActions'
import { DQ_REASONS } from '@/reducers/auth/userReducer'

export const SignupWaitlist = () => {
  const dispatch = useAppDispatch()
  const [submitted, setSubmitted] = useState(false)
  const user = useReselector(getCurrentUser)

  useSignupPageUpdate(SIGNUP_PATHS.waitlist)

  const formik = useFormik({
    initialValues: {
      email: user?.email || '',
      numberOfEmployees: '',
    },
    onSubmit: async ({ email, numberOfEmployees }) => {
      const res = await postWaitlistUser({
        email,
        numberOfEmployees: Number(numberOfEmployees),
      })(dispatch)

      if (res) {
        setSubmitted(true)
      }
    },
  })

  const returnToSite = useCallback(() => {
    window.location.href = 'https://www.joinheard.com'
  }, [])

  useEffect(() => {
    if (!user?.id) {
      return
    }

    updateUser(user.id, {
      disqualifiedReasons: [DQ_REASONS.TOO_MANY_EMPLOYEES],
      disqualifiedAt: new Date().toISOString(),
    })(dispatch)
  }, [dispatch, user?.id])

  const fieldNames = getFieldNames(formik)

  return (
    <SignupPageLayout
      hideProgress
      footer={
        <SignupFooter
          backText="Return to Site"
          onBack={returnToSite}
          continueText="Join Waitlist"
          onContinue={formik.handleSubmit}
        />
      }
    >
      <div className="flex flex-row">
        <div className="self-start flex-4 flex flex-col space-y-4 mx-4">
          <FormikProvider value={formik}>
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/plant-monstera.svg"
              alt="plant"
              height={170}
              width={120}
            />

            <Text variant={TypographyStyles.DISPLAY_MD}>
              {user?.firstName || ''}, congratulations on building a flourishing
              private practice
            </Text>

            <Text variant={TypographyStyles.BODY_SM}>
              Right now, Heard supports private practices with up to 10
              employees.
              <br />
              <br />
              Because your practice is larger, we’re not the best fit today but
              we’d love to stay in touch as we expand support for larger
              practices.
              <br />
              <br />
              Join our waitlist to get updates when we’re able to better support
              practices like yours, plus practical guidance on managing finances
              as you grow.
            </Text>

            <div className="mt-10">
              <Form.Input
                label="Email address to receive updates"
                placeholder="jane.smith@privatepractice.com"
                required
                name={fieldNames.email}
                schema={makeReqEmailSchema({ field: 'email' })}
              />
            </div>

            <div className="mt-2">
              <Form.Input
                label="Total number of employees"
                description="Include yourself as an employee"
                placeholder="Enter number of employees"
                required
                name={fieldNames.numberOfEmployees}
                schema={makeNumberSchema({ field: 'number of employees' })}
              />
            </div>
          </FormikProvider>
        </div>
        <div className="flex-3 self-center">
          <KeepInTouchCard />
        </div>
      </div>
      <Dialog open={submitted}>
        <Dialog.Content>
          <Dialog.Header>Thanks for joining our waitlist.</Dialog.Header>
          <IllustrationBlock
            src="https://heard-images.s3.amazonaws.com/assets/circle-check-mark.svg"
            alt="circle check"
            color={ImageBackgroundColors.GREEN}
            className="mt-4"
            size="sm"
          />
          <Text className="mt-4">
            We’ll reach out if our services change. Good luck!
          </Text>

          <Dialog.Footer>
            <Button onClick={returnToSite}>Return to Site</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </SignupPageLayout>
  )
}
